// Tickets and their repair visits. The list pages on the server. The open
// ticket (`current`) is replaced by whatever ticket a write answers with, so
// the detail page, its buttons (`allowed_steps`) and its history always show
// the server's latest state.
import {
  listTickets,
  getTicket,
  patchTicketStatus,
  postTicketSetStatus,
  uploadTicketMedia,
  deleteTicketMedia,
  putTicketAssignee,
  getAssigneePreview,
  putTicketNotes,
  listTicketMessages,
  createManualTicket,
} from '../../api/coLiving/tickets';
import {
  getSlots,
  createVisit,
  confirmVisit,
  rescheduleVisit,
  cancelVisit,
  completeVisit,
  noShowVisit,
  withdrawVisit,
} from '../../api/coLiving/visits';

export const state = {
  tickets: [],
  meta: { page: 1, pageSize: 10, total: 0 },
  current: null,
  uiFlags: { isFetching: false, isFetchingTicket: false },
};

export const getters = {
  getTickets: s => s.tickets,
  getMeta: s => s.meta,
  getCurrent: s => s.current,
  getUiFlags: s => s.uiFlags,
};

export const mutations = {
  setTickets(s, { results, page, page_size: pageSize, total }) {
    s.tickets = results;
    s.meta = { page, pageSize, total };
  },
  setCurrent(s, ticket) {
    s.current = ticket;
  },
  setUiFlag(s, { key, value }) {
    s.uiFlags = { ...s.uiFlags, [key]: value };
  },
};

// Search and filters fire overlapping requests: only the latest one may write
// the list, or a slow old answer would replace a newer one.
let latestListRequest = 0;

export const actions = {
  async fetchTickets({ commit }, query) {
    latestListRequest += 1;
    const request = latestListRequest;
    commit('setUiFlag', { key: 'isFetching', value: true });
    try {
      const { data } = await listTickets(query);
      if (request === latestListRequest) commit('setTickets', data);
      return data;
    } finally {
      if (request === latestListRequest) {
        commit('setUiFlag', { key: 'isFetching', value: false });
      }
    }
  },
  async fetchTicket({ commit, state: moduleState }, id) {
    // Another ticket's data must not show under this id while it loads, or
    // stay on screen if the load fails.
    if (moduleState.current && moduleState.current.id !== id) {
      commit('setCurrent', null);
    }
    commit('setUiFlag', { key: 'isFetchingTicket', value: true });
    try {
      const { data } = await getTicket(id);
      commit('setCurrent', data);
      return data;
    } finally {
      commit('setUiFlag', { key: 'isFetchingTicket', value: false });
    }
  },
  // The buttons a ticket allows right now, read without touching `current`
  // (the list's status menu asks for a row that is not the open ticket).
  async fetchAllowedSteps(_, id) {
    const { data } = await getTicket(id);
    return data;
  },
  // PATCH /status answers `{from_status, to_status, ticket}`.
  async changeStatus({ commit }, { id, body }) {
    const { data } = await patchTicketStatus(id, body);
    commit('setCurrent', data.ticket);
    return data;
  },
  // POST /set-status answers `{from_status, to_status, reopened, tell,
  // ticket}`; nobody is messaged, `tell` says who the admin may write to.
  async setStatus({ commit }, { id, body }) {
    const { data } = await postTicketSetStatus(id, body);
    commit('setCurrent', data.ticket);
    return data;
  },
  // Photos change the ticket's `media`, so the ticket is read again after.
  async addPhotos({ dispatch }, { id, files }) {
    const { data } = await uploadTicketMedia(id, files);
    await dispatch('fetchTicket', id);
    return data.media;
  },
  async removePhoto({ dispatch }, { id, mediaId }) {
    await deleteTicketMedia(id, mediaId);
    await dispatch('fetchTicket', id);
  },
  async fetchAssigneePreview(_, { id, memberId }) {
    const { data } = await getAssigneePreview(id, memberId);
    return data;
  },
  // PUT /assignee answers `{changed, visits, ticket}`.
  async changeAssignee({ commit }, { id, memberId }) {
    const { data } = await putTicketAssignee(id, memberId);
    commit('setCurrent', data.ticket);
    return data;
  },
  // PUT /notes answers the ticket.
  async saveNotes({ commit }, { id, body }) {
    const { data } = await putTicketNotes(id, body);
    commit('setCurrent', data);
    return data;
  },
  async fetchMessages(_, id) {
    const { data } = await listTicketMessages(id);
    return data.results || [];
  },
  async createTicket(_, fields) {
    const { data } = await createManualTicket(fields);
    return data;
  },
  async fetchSlots(_, { id, params }) {
    const { data } = await getSlots(id, params);
    return data;
  },
  // Every visit call answers `{visit, ticket: {ticket_id, status,
  // status_label}}` and moves the ticket, so the full ticket is read again.
  async sendVisit({ dispatch }, { id, body }) {
    const { data } = await createVisit(id, body);
    await dispatch('fetchTicket', id);
    return data;
  },
  async visitAction({ dispatch }, { ticketId, visitId, action, payload }) {
    const calls = {
      confirm: () => confirmVisit(visitId, payload.option),
      reschedule: () => rescheduleVisit(visitId, payload),
      cancel: () => cancelVisit(visitId, payload && payload.reason),
      complete: () => completeVisit(visitId),
      noShow: () => noShowVisit(visitId, payload && payload.reason),
      withdraw: () => withdrawVisit(visitId, payload && payload.reason),
    };
    const { data } = await calls[action]();
    await dispatch('fetchTicket', ticketId);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
